/**
 * External items become records here, and nowhere else.
 *
 * The output is an ordinary HGTimeRecordPlus, indistinguishable to the record
 * tree from a database row, apart from the `external` marker that makes it
 * read-only. Everything the tree relies on -- both interval forms, both second
 * bounds, the intersection -- is produced by the same `enrichRecords` the API
 * layer uses, so the two can never drift apart.
 */

import { enrichRecords } from "@/api/record";
import { constructInterval } from "@/common/intervals";
import {
  HGExternalMeta,
  HGInterval,
  HGTimeRecord,
  HGTimeRecordPlus,
} from "@/common/types";
import { dateToOrdinal } from "@/common/unixTime";
import { MapInterval } from "@/engine/engine";

import { ExternalItem, ExternalSummary, ProviderConfig } from "./types";

/**
 * ":" is outside the nanoid alphabet the backend generates ids from
 * (0-9a-zA-Z), so a synthetic id can never collide with a real one, and the
 * test for one is a prefix check rather than a lookup.
 */
export const EXTERNAL_ID_PREFIX = "ext:";

export function isExternalRecordId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(EXTERNAL_ID_PREFIX);
}

export function externalRecordId(collectionId: string, externalId: string) {
  return `${EXTERNAL_ID_PREFIX}${collectionId}:${externalId}`;
}

interface BuildOptions {
  collectionId: string;
  providerKey: string;
  userId: string;
  secondsRange: [bigint, bigint];
}

function baseRecord(
  id: string,
  title: string,
  content: string,
  interval: HGInterval,
  endInterval: HGInterval,
  startSecond: bigint,
  endSecond: bigint,
  external: HGExternalMeta,
  options: BuildOptions,
): HGTimeRecord {
  return {
    id,
    title,
    content,
    url: [],
    collection_id: [options.collectionId],
    slug: null,
    created: Date.now(),
    modified: null,
    user_id: options.userId,
    interval,
    end_interval: endInterval,
    start_second: startSecond,
    end_second: endSecond,
    // The row carries its whole text already, so the preview pane renders it
    // without the by-id fetch that would 404 on a synthetic id.
    content_truncated: false,
    external,
  };
}

export async function adaptItems(
  items: ExternalItem[],
  config: ProviderConfig,
  itemUrl: (item: ExternalItem, config: ProviderConfig) => string | undefined,
  options: BuildOptions,
): Promise<HGTimeRecordPlus[]> {
  const records: HGTimeRecord[] = [];

  for (const item of items) {
    const startSecond = dateToOrdinal(item.at);
    // Anything the scale cannot place -- a date before 1970, an unparseable
    // header -- is dropped rather than pinned to an arbitrary moment, where it
    // would look like real data in the wrong year.
    if (startSecond === null) continue;

    const endSecond = item.until ? dateToOrdinal(item.until) : startSecond;
    const resolvedEnd =
      endSecond !== null && endSecond >= startSecond ? endSecond : startSecond;

    const href = itemUrl(item, config);
    const record = baseRecord(
      externalRecordId(options.collectionId, item.externalId),
      item.title,
      item.content,
      constructInterval(startSecond, "s"),
      constructInterval(resolvedEnd, "s"),
      startSecond,
      resolvedEnd,
      {
        providerKey: options.providerKey,
        collectionId: options.collectionId,
        externalId: item.externalId,
        href,
      },
      options,
    );
    // Deliberately not synthesizing a url entry from `href`: RecordBody
    // counts url.length into its "1 link" line, and every row claiming a
    // link it did not have is noise. The permalink is an affordance on the
    // preview, not a piece of the record's content.
    record.url = item.url ?? [];
    records.push(record);
  }

  return enrichRecords(records, options.secondsRange);
}

/**
 * A count for one bucket, as a record whose span *is* that bucket.
 *
 * This is the whole trick behind coarse-zoom rows: the record tree groups by
 * interval and knows nothing about where a record came from, so a summary
 * carrying its bucket's own interval lands in exactly the right place with no
 * new node kind, no change to `buildRecordTree`, and no fourth branch in the
 * renderer beyond reading `external.summary`.
 */
export async function adaptSummary(
  summary: ExternalSummary,
  bucket: HGInterval,
  label: string,
  options: BuildOptions,
): Promise<HGTimeRecordPlus[]> {
  if (summary.count <= 0) return [];

  const span = MapInterval(bucket, "s");
  const record = baseRecord(
    externalRecordId(options.collectionId, `summary:${bucket.it}:${bucket.n}`),
    label,
    "",
    bucket,
    bucket,
    span.begin.n,
    span.end.n,
    {
      providerKey: options.providerKey,
      collectionId: options.collectionId,
      externalId: `summary:${bucket.it}:${bucket.n}`,
      summary: { count: summary.count, estimate: summary.estimate },
    },
    options,
  );

  return enrichRecords([record], options.secondsRange);
}
